import { useState } from "react";
import axios from "axios";
import { Reveal } from "./Reveal";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INQUIRY_TYPES = [
  { value: "speaking", label: "Speaking engagement" },
  { value: "partnership", label: "Partnership / Institutional" },
  { value: "investment", label: "Investment opportunity" },
  { value: "press", label: "Press & media" },
  { value: "general", label: "General inquiry" },
];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    inquiry_type: "general",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.length < 2 || !form.email.includes("@") || form.message.length < 10) {
      toast.error("Please complete all required fields with valid information.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received. The office of Sean Bledsoe will respond within 2 business days.");
      setForm({
        name: "",
        email: "",
        organization: "",
        inquiry_type: "general",
        message: "",
      });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 lg:py-32 bg-white"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-20">
        <Reveal className="lg:col-span-5">
          <div className="flex items-center gap-4 mb-6">
            <span className="h-px w-10 bg-[#0A192F]/40" />
            <span className="text-xs uppercase tracking-[0.3em] text-[#0A192F]/60">
              Contact
            </span>
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-[64px] leading-[1.02] tracking-tight text-[#0A192F]">
            A private line
            <br />
            to the office.
          </h2>
          <p className="mt-10 text-lg text-[#475569] leading-relaxed max-w-md">
            For speaking engagements, board considerations, partnership
            discussions, or press — share a few details and the office will
            respond personally within two business days.
          </p>

          <div className="mt-12 space-y-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#0A192F]/60 mb-2">
                Email
              </div>
              <div className="text-[#0A192F]">sbledsoe1109@gmail.com</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#0A192F]/60 mb-2">
                Located
              </div>
              <div className="text-[#0A192F]">United States · India</div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={150} className="lg:col-span-7">
          <form
            onSubmit={submit}
            className="bg-[#F8FAFC] border border-black/5 p-8 lg:p-12 space-y-6"
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="name" className="text-xs uppercase tracking-[0.25em] text-[#0A192F]/70">
                  Full name *
                </Label>
                <Input
                  id="name"
                  data-testid="contact-input-name"
                  required
                  value={form.name}
                  onChange={onChange("name")}
                  className="mt-2 h-12 bg-white border-[#0A192F]/15 focus-visible:ring-[#0A192F]"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-xs uppercase tracking-[0.25em] text-[#0A192F]/70">
                  Email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="contact-input-email"
                  required
                  value={form.email}
                  onChange={onChange("email")}
                  className="mt-2 h-12 bg-white border-[#0A192F]/15 focus-visible:ring-[#0A192F]"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="organization" className="text-xs uppercase tracking-[0.25em] text-[#0A192F]/70">
                  Organization
                </Label>
                <Input
                  id="organization"
                  data-testid="contact-input-organization"
                  value={form.organization}
                  onChange={onChange("organization")}
                  className="mt-2 h-12 bg-white border-[#0A192F]/15 focus-visible:ring-[#0A192F]"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.25em] text-[#0A192F]/70">
                  Type of inquiry
                </Label>
                <Select
                  value={form.inquiry_type}
                  onValueChange={(v) => setForm((f) => ({ ...f, inquiry_type: v }))}
                >
                  <SelectTrigger
                    data-testid="contact-select-inquiry"
                    className="mt-2 h-12 bg-white border-[#0A192F]/15"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INQUIRY_TYPES.map((o) => (
                      <SelectItem
                        key={o.value}
                        value={o.value}
                        data-testid={`contact-inquiry-option-${o.value}`}
                      >
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="message" className="text-xs uppercase tracking-[0.25em] text-[#0A192F]/70">
                Message *
              </Label>
              <Textarea
                id="message"
                data-testid="contact-input-message"
                required
                rows={6}
                value={form.message}
                onChange={onChange("message")}
                placeholder="Share context — opportunity, timeline, and what would constitute a successful first conversation."
                className="mt-2 bg-white border-[#0A192F]/15 focus-visible:ring-[#0A192F]"
              />
            </div>

            <div className="flex items-center justify-between gap-4 pt-2">
              <p className="text-xs text-[#475569] max-w-sm">
                Confidential. Messages are reviewed by the office; not shared
                with third parties.
              </p>
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={submitting}
                className="inline-flex items-center gap-3 px-7 py-4 bg-[#0A192F] text-white text-sm tracking-wide rounded-full hover:-translate-y-0.5 hover:shadow-2xl transition-all duration-300 disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Send message"}
                <span aria-hidden>→</span>
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
